import { type GetStaticPathsResult, type GetStaticPropsResult } from 'next'
import dynamic from 'next/dynamic'
import { type ParsedUrlQuery } from 'querystring'

import { shopSalePageModuleQuery } from '@data/sanity/queries/page'
import { type SanityShopSalePage } from '@data/sanity/queries/types/page'
import { type SanitySiteFragment } from '@data/sanity/queries/types/site'
import { type Locale, getAllLocales } from '@lib/language'
import {
  type BasePageProps,
  getNonNullValues,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import { type CustomStaticPropsContext } from '@lib/routes'
import { getSanityClient } from '@lib/sanity/client'
import {
  defaultModuleQueryParameters,
  getShopPagePaths,
  getShopSalePage,
} from '@lib/sanity/page'
import { getVideoObjectSchemas } from '@lib/schema/video'
import { getShopifyShopForPageWithCache } from '@lib/shopify/store-cache'

import Layout from '@components/layout'
import Modules from '@modules/modules'
import { useMemo } from 'react'

const PreviewModules = dynamic(() => import('@modules/preview-modules'))

type SaleChildPageProps = BasePageProps & {
  childSlug: string
  page: SanityShopSalePage
  site: SanitySiteFragment
}

type StaticSaleChildPageParams = ParsedUrlQuery & {
  childSlug: string
}

const SaleChildPage = ({
  draftMode,
  locale,
  childSlug,
  page,
  site,
}: SaleChildPageProps) => {
  const schemas = useMemo(() => {
    const videoObjectSchemas = getVideoObjectSchemas(page)
    return [...videoObjectSchemas]
  }, [page])

  return (
    <Layout page={page} site={site} schemas={schemas} draftMode={draftMode}>
      {!draftMode && (
        <Modules
          modules={page.modules}
          pageProducts={page.products}
          pageFeaturedProductIds={page.featuredProductIds}
          parentPageType="shopSalePage"
          childSlug={childSlug}
          quickFilterBasePath="/shop/sale"
          quickFilterAllPath="/shop/sale"
        />
      )}
      {draftMode && (
        <PreviewModules
          modules={page.modules}
          query={shopSalePageModuleQuery}
          params={{
            ...defaultModuleQueryParameters,
            locale,
          }}
          pageProducts={page.products}
          pageFeaturedProductIds={page.featuredProductIds}
          parentPageType="shopSalePage"
          childSlug={childSlug}
          quickFilterBasePath="/shop/sale"
          quickFilterAllPath="/shop/sale"
        />
      )}
    </Layout>
  )
}

export async function getStaticProps(
  context: CustomStaticPropsContext<StaticSaleChildPageParams>,
): Promise<GetStaticPropsResult<SaleChildPageProps>> {
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const shopSalePage = await getShopSalePage(sanityClient, context.locale)

  if (!shopSalePage.page) {
    return {
      notFound: true,
      revalidate: 600,
    }
  }

  const childSlug = context.params?.childSlug ?? ''

  const shopifyShop = await getShopifyShopForPageWithCache(context.locale)

  return {
    props: {
      ...getPagePropsFromContext(context),
      childSlug,
      page: getNonNullValues(shopSalePage.page),
      site: getNonNullValues(shopSalePage.site),
      shopifyShop,
    },
    revalidate: 600,
  }
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<StaticSaleChildPageParams>
> {
  if (process.env.NEXT_PUBLIC_APP_ENV === 'preview') {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }

  const sanityClient = getSanityClient()
  const allLocales = getAllLocales()
  const shopPagePaths = await getShopPagePaths(
    sanityClient,
    allLocales,
    'shopSalePage',
  )

  const paths = shopPagePaths
    .filter((shopPage) => {
      return allLocales.includes(shopPage.locale as Locale)
    })
    .flatMap((shopPage) => {
      return shopPage.modules.flatMap((module) => {
        const featuredCollections = module.featuredCollections ?? []

        return featuredCollections
          .filter((featuredCollection) => {
            return !!featuredCollection.slug.current
          })
          .map((featuredCollection) => {
            return {
              params: {
                childSlug: featuredCollection.slug.current,
              },
              locale: shopPage.locale,
            }
          })
      })
    })

  return {
    paths,
    fallback: 'blocking',
  }
}

export default SaleChildPage
